import { CallScheduleDto } from '@shared/services/apiService/apiService';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isoWeek from 'dayjs/plugin/isoWeek';
import timezone from 'dayjs/plugin/timezone';
import isBetween from 'dayjs/plugin/isBetween';
import 'dayjs/locale/en';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
dayjs.extend(isBetween);
dayjs.locale('en');

const DEFAULT_TIMEZONE = 'Asia/Dubai';
const dayMap = {
  0: { start: 'sundayStart', end: 'sundayEnd' },
  1: { start: 'mondayStart', end: 'mondayEnd' },
  2: { start: 'tuesdayStart', end: 'tuesdayEnd' },
  3: { start: 'wednesdayStart', end: 'wednesdayEnd' },
  4: { start: 'thursdayStart', end: 'thursdayEnd' },
  5: { start: 'fridayStart', end: 'fridayEnd' },
  6: { start: 'saturdayStart', end: 'saturdayEnd' },
};

export const currentTimestamp = () => {
  return dayjs().millisecond(0);
};

const normalizeScheduleTime = (time: string): string => {
  const parts = time.split(':');

  if (parts.length === 2) {
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:00`;
  }

  if (parts.length >= 3) {
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:${parts[2].padStart(2, '0')}`;
  }

  return time;
};

const parseScheduleTimeOnDay = (
  day: dayjs.Dayjs,
  time: string,
  timezone: string,
): dayjs.Dayjs | null => {
  const normalized = normalizeScheduleTime(time);
  const parsed = dayjs.tz(
    `${day.format('YYYY-MM-DD')} ${normalized}`,
    'YYYY-MM-DD HH:mm:ss',
    timezone,
  );

  return parsed.isValid() ? parsed : null;
};

const getTodayCallSchedule = (currentDay: number, schedule: CallScheduleDto) => {
  const { start, end } = dayMap[currentDay as keyof typeof dayMap];
  const startValue = schedule[start as keyof CallScheduleDto];
  const endValue = schedule[end as keyof CallScheduleDto];
  return {
    start: startValue,
    end: endValue,
  };
};

const getDayOfWeekIndex = (date: dayjs.Dayjs): number => {
  return date.day();
};

export const isOutOfSchedule = (schedule: CallScheduleDto, now: Date = new Date()): boolean => {
  const scheduleTimezone = schedule.timezone || DEFAULT_TIMEZONE;
  const nowInZone = dayjs(now).tz(scheduleTimezone);
  const dayIndex = getDayOfWeekIndex(nowInZone);
  const { start, end } = getTodayCallSchedule(dayIndex, schedule);

  if (!start || !end) {
    return true;
  }

  const startTime = parseScheduleTimeOnDay(nowInZone, start, scheduleTimezone);
  const endTime = parseScheduleTimeOnDay(nowInZone, end, scheduleTimezone);

  if (!startTime || !endTime) {
    return true;
  }

  // Match API schedule validation: inclusive start, exclusive end.
  return !nowInZone.isBetween(startTime, endTime, 'second', '[)');
};

const SCHEDULE_LOOKAHEAD_DAYS = 14;

export const getNextAvailableCallTimestamp = (
  schedule: CallScheduleDto | undefined,
  referenceDate: Date = new Date(),
): number | null => {
  if (!schedule) {
    return null;
  }

  const scheduleTimezone = schedule.timezone || DEFAULT_TIMEZONE;
  const now = dayjs(referenceDate).tz(scheduleTimezone);

  for (let i = 0; i < SCHEDULE_LOOKAHEAD_DAYS; i++) {
    const candidateDay = now.add(i, 'day');
    const dayIndex = getDayOfWeekIndex(candidateDay);
    const { start } = getTodayCallSchedule(dayIndex, schedule);

    if (!start) {
      continue;
    }

    const targetDate = parseScheduleTimeOnDay(candidateDay, start, scheduleTimezone);

    if (!targetDate || targetDate.valueOf() <= now.valueOf()) {
      continue;
    }

    return targetDate.valueOf();
  }

  return null;
};
